import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  Image,
  ActivityIndicator,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import {
  openStoreListing,
  platformFromRoute,
  storeListingForPlatform,
} from '../constants/appUpdate';
import { useAuth } from '../contexts/AuthContext';
import { colors } from '../theme/colors';

const APP_STORE_BADGE = require('../assets/store-badges/download-on-the-app-store.png');
const PLAY_STORE_BADGE = require('../assets/store-badges/google-play-badge.png');

interface AppUpdateProps {
  navigate: (path: string) => void;
  route?: string;
}

export const AppUpdate: React.FC<AppUpdateProps> = ({
  navigate,
  route = '',
}) => {
  const { isAuthenticated, login } = useAuth();
  const platform = platformFromRoute(route);
  const inNativeFrame = platform !== null;
  const listing = storeListingForPlatform(platform);
  const storeBadge = platform === 'android' ? PLAY_STORE_BADGE : APP_STORE_BADGE;
  const [continuing, setContinuing] = useState(false);
  const [continueError, setContinueError] = useState<string | null>(null);

  const continueOnWebsite = async () => {
    if (isAuthenticated) {
      navigate('/');
      return;
    }

    setContinueError(null);
    setContinuing(true);
    try {
      await login();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Sign-in could not be started';
      setContinueError(message);
      setContinuing(false);
    }
  };

  return (
    <SafeAreaView
      style={styles.container}
      {...(inNativeFrame ? { edges: ['bottom'] as const } : {})}
    >
      <View
        testID={
          inNativeFrame ? 'app-update-phone-layout' : 'app-update-web-layout'
        }
        style={[styles.content, inNativeFrame && styles.phoneContent]}
      >
        <Text style={styles.eyebrow}>SITE SCOPE</Text>
        <Text style={styles.title}>Update required</Text>
        <Text style={styles.message}>
          You are on an older version and must update before you can continue.
        </Text>
        <Pressable
          accessibilityRole="link"
          accessibilityLabel={listing.label}
          accessibilityHint={listing.url}
          style={styles.storeBadge}
          onPress={() => openStoreListing(listing.url)}
        >
          <Image
            source={storeBadge}
            style={
              platform === 'android' ? styles.playBadge : styles.appStoreBadge
            }
            resizeMode="contain"
            accessible={false}
          />
        </Pressable>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Continue on the website"
          style={styles.continueLink}
          onPress={() => {
            void continueOnWebsite();
          }}
          disabled={continuing}
        >
          {continuing ? (
            <ActivityIndicator color={colors.primary} />
          ) : (
            <Text style={styles.continueLinkText}>Continue on the website</Text>
          )}
        </Pressable>
        {continueError ? (
          <Text style={styles.error}>{continueError}</Text>
        ) : null}
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.backgroundPrimary,
  },
  content: {
    flex: 1,
    justifyContent: 'center',
    maxWidth: 420,
    width: '100%',
    alignSelf: 'center',
    paddingHorizontal: 24,
    paddingVertical: 32,
  },
  phoneContent: {
    maxWidth: '100%',
    alignSelf: 'stretch',
    justifyContent: 'flex-start',
    paddingTop: 24,
  },
  eyebrow: {
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 1,
    color: colors.textSecondary,
    marginBottom: 8,
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
    color: colors.textPrimary,
    marginBottom: 12,
  },
  message: {
    fontSize: 16,
    lineHeight: 24,
    color: colors.textSecondary,
    marginBottom: 24,
  },
  storeBadge: {
    alignSelf: 'flex-start',
  },
  appStoreBadge: {
    width: 162,
    height: 54,
  },
  playBadge: {
    width: 188,
    height: 73,
  },
  continueLink: {
    marginTop: 16,
    alignSelf: 'flex-start',
    paddingVertical: 8,
  },
  continueLinkText: {
    color: colors.primary,
    fontSize: 16,
    fontWeight: '700',
  },
  error: {
    marginTop: 12,
    color: colors.textPrimary,
    fontSize: 14,
  },
});
